import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, type ReactNode } from 'react';
import { usePrefs } from '../../app/prefs';
import { useSiteScope } from '../../app/scope';
import { useReportScope } from '../reports/ReportScope';
import { LoginPage } from './LoginPage';
import { applyUserPrefs, useMe } from './session';

/** Renders the application only for an authenticated session; otherwise the login page. */
export function AuthGate({ children }: { children: ReactNode }) {
  const me = useMe();
  const qc = useQueryClient();
  const userId = useRef<string | null>(null);
  useEffect(() => {
    if (me.data) applyUserPrefs(me.data);
    else if (me.data === null) usePrefs.getState().setSignedIn(false);
    // The session ended or another user signed in (sign-out button, expired session, login in the same tab):
    // nothing cached for the previous user may be shown to the next one. Devices and counters are cleared when the
    // realtime layer unmounts; here the query cache and the selected site / client.
    const id = me.data?.id ?? null;
    if (userId.current !== null && userId.current !== id) {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' && q.queryKey[0] !== 'branding' });
      useSiteScope.setState({ siteId: null });
      useReportScope.setState({ clientId: null, projectId: null });
    }
    if (me.data !== undefined) userId.current = id;
  }, [me.data, qc]);
  if (me.isLoading) return <div className="splash" aria-busy />;
  if (!me.data) return <LoginPage />;
  return <>{children}</>;
}
